<template>
    <div class="c-transfer-panel">
        <div class="c-transfer-panel__header">
            <slot name="header">
                <span>{{ title }}</span>
            </slot>
            <span class="c-transfer-panel__checkall">
                <el-checkbox v-if="showCheckAll" v-model="allChecked" @change="handleAllCheckedChange"
                    :indeterminate="isIndeterminate">
                    <span>{{ $t('wuliu.transfer.checkAll') }}</span>

                </el-checkbox>
                <i18n path="wuliu.transfer.totalCount" tag="span" class="transfer-checked-sum">
                    <span place="count" style=" color: #50F5FF;">{{ checkedSummary }}</span>
                </i18n>
            </span>
        </div>
        <div class="c-transfer-panel__body" :style="mainStyle" v-loading="loading" :element-loading-text="$t('wuliu.common.loading')"
            element-loading-spinner="el-icon-loading" element-loading-background="rgba(0, 0, 0, 0.5)">
            <div v-if="filterable" style="padding: 10px 10px 5px 10px;">
                <el-input class="c-transfer-panel__filter c-el-input-blue" v-model="query" size="small"
                    :placeholder="placeholder" clearable>
                    <i slot="prefix" :class="['el-input__icon', 'el-icon-search']"></i>

                    <template slot="append" v-if="query.length > 0">
                        <span style="font-size: 12px;padding:0 8px">{{ $t('wuliu.transfer.totalCount', { count: filteredData.length }) }}</span>
                    </template>
                </el-input>
            </div>

            <CustomElScrollbar v-if="data.length > 0" :style="{ height: filterable ? 'calc(100% - 60px)' : '100%' }">
                <el-checkbox-group v-if="data.length > 0 && multiple" v-model="checked" class="c-transfer-panel__list">
                    <div class="c-transfer-panel__item" v-for="item in filteredData" :key="item[keyProp]">
                        <el-checkbox :label="item[keyProp]" :disabled="item[disabledProp]">
                            <option-content :option="item" :customProps="customProps"></option-content>
                        </el-checkbox>
                    </div>
                </el-checkbox-group>
                <div v-else class="c-transfer-panel__list">
                    <div v-for="item in filteredData" :key="item[keyProp]" class="c-transfer-panel__item"
                        :class="{ 'is-singlecheck': !multiple, 'is-disabled': item[disabledProp] }">
                        <span v-if="arrowConfig.position === 'left'" class="arrow-container " style="left: 0px;"
                            @click.prevent="onArrowBtnClick(item)">
                            <i :class="arrowConfig.icon" :style="'float: ' + arrowConfig.iconPosition"></i>
                            {{ arrowConfig.name }}
                        </span>
                        <div style="flex: 1">
                            <option-content :option="item" :customProps="customProps"></option-content>
                        </div>
                        <span v-if="arrowConfig.position === 'right'" class="arrow-container " style="right: 0px;"
                            @click.prevent="onArrowBtnClick(item)">
                            {{ arrowConfig.name }}
                            <i :class="arrowConfig.icon" :style="'float: ' + arrowConfig.iconPosition"></i>
                        </span>
                    </div>

                </div>

            </CustomElScrollbar>
            <el-empty v-if="data.length <= 0" :description="emptyText" style="height:100%">
                <div slot="image"></div>
            </el-empty>
        </div>
        <div class="c-transfer-panel__footer" v-if="hasFooter" :style="footerStyle">
            <slot name="footer">

            </slot>
        </div>
    </div>
</template>

<script>
import OptionContent from './OptionContent.js';
import CustomElScrollbar from '@/components/custom-el-scrollbar/CustomElScrollbar.vue';
import { throttle } from '@/utils/common.js';

export default {
    name: 'TransferPanel',
    components: {
        OptionContent,
        CustomElScrollbar,
    },
    props: {
        title: String,
        data: {
            type: Array,
            default() {
                return [];
            },
        },
        props: Object,
        filterMethod: Function,
        renderContent: Function,
        filterable: Boolean,
        placeholder: String,
        /**
     * 列表顶部勾选状态文案配置
     * { noChecked: '${checked}/${total}', hasChecked: '${checked}/${total}' }
     */
        format: {
            type: Object,
            default() {
                return {};
            },
        },
        /**
         * 是否支持多选，默认为 true,值为true 时显示checkbox
         */
        multiple: {
            type: Boolean,
            default: true,
        },
        // 穿梭按钮配置信息，包含位置（左右）、名称、图标位置，图标 等
        arrowConfig: {
            type: Object,
            default() {
                return {
                    position: 'left',
                    name: this.$t('wuliu.transfer.toLeft'),
                    iconPosition: 'left',
                    icon: 'el-icon-arrow-left',
                };
            },
        },

        /**
         * 是否显示全选复选框
         */
        showCheckAll: {
            type: Boolean,
            default: true,
        },
        footerHeight: {
            type: Number,
            default: 40,
        },
        /**
         * 列表是否处于加载中状态
         */
        loading: {
            type: Boolean,
            default: false,
        },
        emptyText: {
            type: String,
            default() {
                return this.$t('wuliu.transfer.noData');
            },
        },
        /**
         * 自定义属性
         * */
        customProps: {
            type: Object,
            default() {
                return {};
            },
        },
    },

    data() {
        return {
            checked: [],
            allChecked: false,
            query: '',
            checkChangeByUser: true,

        };
    },
    computed: {
        filteredData() {
            return this.data.filter((item) => {
                if (typeof this.filterMethod === 'function') {
                    return this.filterMethod(this.query, item);
                } else {
                    const label = item[this.labelProp] || item[this.keyProp].toString();
                    return label.toLowerCase().indexOf(this.query.toLowerCase()) > -1;
                }
            });
        },
        labelProp() {
            return this.props.label || 'label';
        },
        keyProp() {
            return this.props.key || 'key';
        },
        disabledProp() {
            return this.props.disabled || 'disabled';
        },

        /**
    * 从 filteredData 中筛选出未被禁用的数据
    *
    * @returns 返回未被禁用的数据数组
    */
        checkableData() {
            return this.filteredData.filter((item) => !item[this.disabledProp]);
        },
        isIndeterminate() {
            const checkedLength = this.checked.length;
            return checkedLength > 0 && checkedLength < this.checkableData.length;
        },
        checkedSummary() {
            const checkedLength = this.checked.length;
            const dataLength = this.data.length;
            const { noChecked, hasChecked } = this.format;
            if (noChecked && hasChecked) {
                return checkedLength > 0
                    ? hasChecked.replace(/\${checked}/g, checkedLength).replace(/\${total}/g, dataLength)
                    : noChecked.replace(/\${total}/g, dataLength);
            } else {
                return `${checkedLength}/${dataLength}`;
            }
        },
        hasFooter() {
            return !!this.$slots.footer;
        },
        footerStyle() {
            return { height: `${this.footerHeight}px` };
        },
        mainStyle() {
            // return { height: `calc(100% - ${this.footerHeight}px - 100px)` };
            let headerHeight = '40px';
            if (this.hasFooter) {
                return { height: `calc(100%  - ${headerHeight} - ${this.footerHeight}px)` };
            } else {
                return { height: `calc(100% - ${headerHeight})` };
            }
        },

    },
    watch: {
        checked(val, oldVal) {
            this.updateAllChecked();
            if (this.checkChangeByUser) {
                const movedKeys = val.concat(oldVal)
                    .filter((v) => val.indexOf(v) === -1 || oldVal.indexOf(v) === -1);
                this.$emit('checked-change', val, movedKeys);
            } else {
                this.$emit('checked-change', val);
                this.checkChangeByUser = true;
            }
        },
        data() {
            this.checked = [];
        },
    },
    methods: {
        updateAllChecked() {
            const checkableDataKeys = this.checkableData.map((item) => item[this.keyProp]);
            this.allChecked = checkableDataKeys.length > 0
                && checkableDataKeys.every((item) => this.checked.indexOf(item) > -1);
        },

        handleAllCheckedChange(value) {
            this.checked = value
                ? this.checkableData.map((item) => item[this.keyProp])
                : [];
        },
        handleArrowClick(item) {
            const disabled = item[this.disabledProp];
            if (disabled) {
                return;
            }
            const keyValue = item[this.keyProp];
            this.checked = [keyValue];
            this.$nextTick(() => {
                this.$emit('arrowButtonClick', keyValue, item);
            });
        },

    },
    created() {
        this.onArrowBtnClick = throttle(this.handleArrowClick, 1000);
    },

    mounted() { },

    beforeDestroy() {
        this.checkChangeByUser = false;
    },
};
</script>

<style lang="scss" scoped>
$bgColor: #0D1541;
$bgColor2: #0D134F;
$colorLight: #5bf0fc;
$borderColor: #2e355b;

.c-transfer-panel {
    background-color: #ffffff;
    height: 100%;
    min-width: 200px;
    position: relative;

    &__header {
        height: 40px;
        line-height: 40px;
        // padding: 0px 20px;
        position: relative;

    }

    &__checkall {
        position: absolute;
        right: 20px;
        top: 0px;

        ::v-deep.el-checkbox__input:not(.is-checked)+.el-checkbox__label {
            color: rgba(255, 255, 255, 0.7);
        }

        .transfer-checked-sum {
            display: inline;
            margin-left: 10px;
        }
    }

    &__body {
        position: relative;
        min-height: 200px;
        height: calc(100% - 140px);
        overflow: hidden
    }

    &__list {
        // padding: 10px;
        background: #0A2066;
        // border-bottom: 1px solid #1E76ED;
    }

    &__item {
        overflow: hidden;
        position: relative;
        margin: 10px;
        border-bottom: 1px solid #1E76ED;

        ::v-deep .el-checkbox {
            display: flex;
            align-items: center;

            &__input {
                flex-shrink: 0;
            }

            &__label {
                width: 0px;
                flex: 1
            }
        }

        &.is-singlecheck {
            display: flex;
            align-items: center;
        }

        .arrow-container {
            flex-shrink: 0;
            padding: 6px;
            cursor: pointer;
            background: rgba(73, 240, 253, 0.1);
            border-radius: 2px;
            border: 1px solid #49F0FD;
            font-size: 12px;
            overflow: hidden;
            // line-height: 9px;
            position: absolute;
            color: #50F5FF;
            // right: 6px;
        }

        &.is-disabled {
            .arrow-container {
                cursor: not-allowed;
                background-color: #BEBEBE40;
                border: 1px solid #BEBEBE;
                color: #BEBEBE;
            }

        }
    }

    &__item:last-child {
        border-bottom: none;
    }

    &__footer {
        height: 40px;
        line-height: 40px;
        padding: 0px 20px;
        border-top: 1px solid $borderColor;
        background-color: $bgColor;
    }

}
</style>
